import { MenuItem, Select } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";

/** Pages that exist in both versions. */
const PAGES = [
  "/",
  "/about",
  "/portfolio",
  "/experience",
  "/certification",
  "/education",
  "/contact",
];

/**
 * v1 / v2 site switcher.
 * - v2 (default): the latest site, with all the animation work — served at `/`.
 * - v1: the earlier pre-animation snapshot, served under `/v1`.
 *
 * Switching keeps you on the same page — only the version prefix changes,
 * e.g. `/experience` ⇄ `/v1/experience`. Unknown paths (like the demo route)
 * fall back to the target version's home.
 */
export default function VersionSwitch() {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const normalized = pathname.replace(/\/+$/, "") || "/";
  const isV1 = normalized === "/v1" || normalized.startsWith("/v1/");
  const version = isV1 ? "v1" : "v2";

  // Strip the version prefix to get the page, e.g. "/v1/about" -> "/about".
  const candidate = isV1 ? normalized.slice("/v1".length) || "/" : normalized;
  const page = PAGES.includes(candidate) ? candidate : "/";

  const pathFor = (target: string) =>
    target === "v1" ? (page === "/" ? "/v1" : `/v1${page}`) : page;

  return (
    <Select
      size="small"
      value={version}
      onChange={(event) => navigate(pathFor(event.target.value))}
      // `inputProps` lands the name on the element that actually carries
      // role="combobox". Putting aria-label on <Select> puts it on the root
      // div, which has no role (Lighthouse: aria-input-field-name +
      // aria-prohibited-attr).
      inputProps={{ "aria-label": "Site version" }}
      title="Switch site version"
      sx={{
        minWidth: 84,
        fontSize: 13,
        fontWeight: 600,
        color: (theme) => theme.palette.text.primary,
        "& .MuiSelect-select": { py: 0.75 },
        "& fieldset": { borderColor: (theme) => theme.palette.primary.main },
      }}
    >
      <MenuItem value="v2">v2</MenuItem>
      <MenuItem value="v1">v1</MenuItem>
    </Select>
  );
}
